import { useEffect, useState } from "react";
import { CurrencyInr, Plus, Trash, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS, PAYMENT_MODES, inr } from "@/lib/constants";
import { PageHeader, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { amount: "", mode: "NEFT", paid_on: today(), reference: "", notes: "" };

export default function PaymentTracking() {
  const [invoices, setInvoices] = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const record = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/invoices/${target.id}/payments`, { ...form, amount });
      toast.success("Payment recorded");
      setTarget(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removePayment = async (invId, payId) => {
    try { await api.delete(`/invoices/${invId}/payments/${payId}`); toast.success("Payment removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!invoices) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading payment tracking…</div>;

  const billable = invoices.filter((i) => i.status !== "cancelled");
  const totalBilled = billable.reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = billable.reduce((s, i) => s + (i.amount_paid || (i.status === "paid" ? i.total : 0) || 0), 0);
  const openOnes = billable.filter((i) => i.status !== "paid");

  return (
    <div className="space-y-6" data-testid="payment-tracking-page">
      <PageHeader overline="Invoices" title="Payment Tracking" subtitle="Record receipts against invoices — partial payments supported" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Billed" value={inr(totalBilled)} testid="stat-billed" />
        <Stat label="Received" value={inr(totalPaid)} tone="text-emerald-600" testid="stat-received" />
        <Stat label="Balance" value={inr(totalBilled - totalPaid)} tone="text-amber-600" testid="stat-balance" />
        <Stat label="Open invoices" value={openOnes.length} testid="stat-open-invoices" />
      </div>

      {billable.length === 0 ? (
        <EmptyState icon={Receipt}>No invoices yet. Raise one from the Invoices screen first.</EmptyState>
      ) : (
        <div className="space-y-3">
          {billable.map((i) => {
            const paid = i.amount_paid || 0;
            const balance = Math.max(0, (i.total || 0) - paid);
            const pct = i.total ? Math.min(100, Math.round((paid / i.total) * 100)) : 0;
            return (
              <Card key={i.id} className="p-5 border-border" data-testid={`pt-invoice-${i.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{i.invoice_number}</div>
                    <div className="font-display font-semibold text-lg text-primary truncate">{i.bill_to_name}</div>
                    <div className="text-xs text-muted-foreground">Issued {(i.issue_date || "").slice(0, 10)}{i.due_date ? ` · due ${i.due_date.slice(0, 10)}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${INVOICE_STATUS_COLORS[i.status]}`}>{INVOICE_STATUS_LABELS[i.status]}</span>
                    <div className="font-display font-black text-xl text-primary tabular mt-1">{inr(i.total)}</div>
                    <div className="text-xs text-muted-foreground">Balance {inr(balance)}</div>
                  </div>
                </div>

                <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                </div>

                {(i.payments || []).length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid={`pt-history-${i.id}`}>
                    {i.payments.map((p) => (
                      <div key={p.id} className="flex flex-wrap items-center gap-2 text-xs p-2 rounded bg-muted border border-border">
                        <span className="font-semibold text-primary tabular">{inr(p.amount)}</span>
                        <span className="text-muted-foreground">{p.mode}</span>
                        <span className="text-muted-foreground font-mono">{p.paid_on}</span>
                        {p.reference && <span className="text-muted-foreground font-mono">UTR {p.reference}</span>}
                        <span className="text-muted-foreground ml-auto">by {p.recorded_by}</span>
                        <button className="text-destructive" onClick={() => removePayment(i.id, p.id)} data-testid={`pt-del-${p.id}`}><Trash size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}

                {i.status !== "paid" && (
                  <Button size="sm" variant="outline" className="rounded-full mt-3"
                    onClick={() => { setTarget(i); setForm({ ...EMPTY, amount: balance ? String(balance) : "" }); }}
                    data-testid={`pt-record-${i.id}`}>
                    <Plus size={14} className="mr-1" /> Record Payment
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Record Payment</DialogTitle>
            <DialogDescription>{target?.invoice_number} · {target?.bill_to_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={record} className="space-y-3" data-testid="payment-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount (₹) *</Label><Input type="number" step="0.01" required value={form.amount} onChange={set("amount")} data-testid="payment-amount" /></div>
              <div className="space-y-1.5"><Label>Mode</Label>
                <Select value={form.mode} onValueChange={set("mode")}>
                  <SelectTrigger data-testid="payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Paid On</Label><DatePicker value={form.paid_on} onChange={set("paid_on")} placeholder="Select date" className="w-full" data-testid="payment-date" /></div>
              <div className="space-y-1.5"><Label>UTR / Reference</Label><Input value={form.reference} onChange={set("reference")} data-testid="payment-reference" /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Input value={form.notes} onChange={set("notes")} /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-payment-btn"><CurrencyInr size={16} className="mr-1" /> Record Payment</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
